import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLayout } from "../../contexts/LayoutContext";
import ResizablePanel from "../../components/common/ResizablePanel";
import PropertyListPanel from "./panels/PropertyListPanel";
import { PROPERTY_COLLECTION_KEYS } from "../../graphQL/properties";
import { toPropertyCard, newPropertyDraft } from "./presenters";
import { usePropertyCollection } from "./usePropertyCollection";
import PropertyContactListPanel from "./panels/PropertyContactListPanel";
import ContactDetailPanel from "../addressbook/panels/ContactDetailPanel";
import CommentsPanel from "../addressbook/panels/CommentsPanel";
import JournalPanel from "../addressbook/panels/JournalPanel";
import RemindersPanel from "../addressbook/panels/RemindersPanel";
import PropertyDetailPanel from "./panels/PropertyDetailPanel";
import PropertyChatPanel from "./panels/PropertyChatPanel";
import PropertyMapPanel from "./panels/PropertyMapPanel";
import { apiCreatePropertyAsset, apiUpdatePropertyAsset, apiDeletePropertyAsset, apiGetPropertyAsset } from "../../graphQL/properties/collections/propertyAssets";
import { apiCreateContact, apiUpdateContact, apiDeleteContact, apiGetContactById } from "../../graphQL/addressbook/collections/contacts";
import ComposeWithMagicDialog from "../../components/spark/ComposeWithMagicDialog";
import ComposeTextDialog from "../../components/spark/ComposeTextDialog";
import SendEmailToCategoryDialog from "../addressbook/panels/SendEmailToCategoryDialog";
import { usePropertyCategories } from "./usePropertyCategories";
import { usePropertyStateList } from "./usePropertyStateList";
import { usePropertyStateCounts } from "./usePropertyStateCounts";
import FilesPanel from "./panels/FilesPanel";
import PropertyImagesPanel from "./panels/PropertyImagesPanel";
import PropertyVerifyPanel from "./panels/PropertyVerifyPanel";
import IngestFileDialog from "./panels/IngestFileDialog";
import { useUploadContext } from "../../Provider/UploadContext";
import { apiUpdatePropertyAsset as apiUpdatePropertyAssetForFiles } from "../../graphQL/properties/collections/propertyAssets";
import { SELECT_ENTITY_JOURNAL_EVENT } from "../../utils/constants";

const PROPERTY_REF_COLLECTION = "propertyAssets";
const PROPERTIES_SELECTED_PROPERTY_KEY = "contextstory.properties.selectedPropertyId";

const formatPropertyLabel = (property) => {
  const address = property?.identity?.address || {};
  const street = [address.streetNo, address.streetName].filter(Boolean).join(" ") || address.street || "";
  const apn = property?.identity?.apn || "";
  return street || property?.name || apn || "";
};

const AVAILABLE_PROPERTY_STATUSES = [
  "Available",
  "Under Contract",
  "Sold",
  "Leased",
  "Off Market",
];

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const COLLECTION_KEY = PROPERTY_COLLECTION_KEYS.PROPERTY_ASSETS;
const EMPTY_CONTACT_DRAFT = {
  identity: { person: { firstName: "", lastName: "" }, company: {} },
  reach: { emails: [], phones: [], social: [], addresses: [] },
};

const PageProperties = () => {
  const user = useSelector((state) => state.auth.user);
  const authorId = useSelector((state) => state.auth.userId);
  const userDomain = String(user?.domain || "").trim();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isPanelOpen, togglePanel, setPanels, setPropertiesToolbar, resetPropertiesToolbar } = useLayout();
  const { uploadSingle } = useUploadContext() || {};

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [propertyStateFilter, setPropertyStateFilter] = useState("");
  const [propertyStatusFilter, setPropertyStatusFilter] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  // Full document (incl. `details`) for the selected property. List items are
  // slim (no `details`), so detail-side panels hydrate from a single-doc fetch.
  const [selectedPropertyFull, setSelectedPropertyFull] = useState(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [composeContact, setComposeContact] = useState(null);
  const [bulkEmail, setBulkEmail] = useState({ open: false, recipients: [] });
  const [composeTextContact, setComposeTextContact] = useState(null);
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);

  const contactsOpen = isPanelOpen("contacts");
  const detailsOpen = isPanelOpen("details");
  const mapOpen = isPanelOpen("map");
  const commentsOpen = isPanelOpen("comments");
  const chatOpen = isPanelOpen("chat");
  const journalOpen = isPanelOpen("journal");
  const remindersOpen = isPanelOpen("reminders");
  const filesOpen = isPanelOpen("files");
  const imagesOpen = isPanelOpen("images");
  const verifyOpen = isPanelOpen("verify");

  // Files panel state
  const [filesDisplayName, setFilesDisplayName] = useState("");
  const [filesSelectedType, setFilesSelectedType] = useState("");
  const [filesSelectedFile, setFilesSelectedFile] = useState(null);
  const [filesStatus, setFilesStatus] = useState("");
  const [filesProgress, setFilesProgress] = useState(0);

  // Details, Map, Comments, Chat, and Journal share the same right-side slot
  // on /properties: only one may be open at a time. Most recently opened wins;
  // on mount with multiple from saved state, priority is Details > Map >
  // Comments > Chat > Journal.
  const slotKeys = ["details", "map", "comments", "chat", "journal", "images"];
  const slotStates = { details: detailsOpen, map: mapOpen, comments: commentsOpen, chat: chatOpen, journal: journalOpen, images: imagesOpen };
  const prevSlotStatesRef = useRef({ details: false, map: false, comments: false, chat: false, journal: false, images: false });
  useEffect(() => {
    const prev = prevSlotStatesRef.current;
    const openKeys = slotKeys.filter((k) => slotStates[k]);
    if (openKeys.length > 1) {
      const justOpened = slotKeys.find((k) => slotStates[k] && !prev[k]);
      const winner = justOpened || openKeys[0]; // priority order via slotKeys
      const next = {};
      openKeys.forEach((k) => { if (k !== winner) next[k] = false; });
      if (Object.keys(next).length) setPanels(next);
    }
    prevSlotStatesRef.current = { ...slotStates };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen, mapOpen, commentsOpen, chatOpen, journalOpen, imagesOpen, setPanels]);
  const lastPanel =
    verifyOpen ? "verify" :
    imagesOpen ? "images" :
    isPanelOpen("files") ? "files" :
    remindersOpen ? "reminders" :
    journalOpen ? "journal" :
    chatOpen ? "chat" :
    commentsOpen ? "comments" :
    mapOpen ? "map" :
    detailsOpen ? "details" :
    contactsOpen ? "contacts" :
    null;

  const pagination = useMemo(() => ({ page, limit: rowsPerPage }), [page, rowsPerPage]);
  const ordering = useMemo(() => ({ field: "updatedAt", order: "Desc" }), []);
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";

  // When user clicks the "N props" chip on a contact card, we filter the
  // property list to that contact's propertyListIds set so they can see
  // everything that contact is linked to without leaving the page.
  const [linkedContactFilter, setLinkedContactFilter] = useState(null);
  // ^ shape: { contactId, contactName, propertyIds: string[] } | null

  const filters = useMemo(() => {
    const f = {};
    if (linkedContactFilter?.propertyIds?.length) {
      // ids-only filter; other filters still narrow within the linked set.
      f.ids = linkedContactFilter.propertyIds;
    }
    if (searchFilter) f.query = searchFilter;
    if (propertyStatusFilter) f.status = propertyStatusFilter;
    if (propertyStateFilter) f.state = propertyStateFilter;
    if (activeDomain) f.domain = activeDomain;
    return f;
  }, [searchFilter, propertyStatusFilter, propertyStateFilter, activeDomain, linkedContactFilter]);
  const selectedPropertyStorageKey = useMemo(
    () => `${PROPERTIES_SELECTED_PROPERTY_KEY}:${activeDomain || "all"}`,
    [activeDomain]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const savedId = window.localStorage.getItem(selectedPropertyStorageKey);
      if (!savedId) return;
      setSelectedProperty((prev) => prev || { id: savedId });
    } catch {
      // no-op
    }
  }, [selectedPropertyStorageKey]);

  // A reminder row asked to open a property's journal while this page is
  // already mounted (localStorage restore only runs on mount).
  useEffect(() => {
    const handler = (e) => {
      const { collection, id } = e.detail || {};
      if (collection !== PROPERTY_REF_COLLECTION || !id) return;
      setSelectedProperty({ id });
    };
    window.addEventListener(SELECT_ENTITY_JOURNAL_EVENT, handler);
    return () => window.removeEventListener(SELECT_ENTITY_JOURNAL_EVENT, handler);
  }, []);
  const activeFilters = useMemo(() => {
    const list = [];
    if (linkedContactFilter) list.push(`linkedTo=${linkedContactFilter.contactName}`);
    if (categoryFilter) list.push(`category=${categoryFilter}`);
    if (propertyStatusFilter) list.push(`property.status=${propertyStatusFilter}`);
    if (propertyStateFilter) list.push(`property.state=${propertyStateFilter}`);
    if (searchFilter) list.push(`query="${searchFilter}"`);
    return list;
  }, [searchFilter, categoryFilter, propertyStatusFilter, propertyStateFilter, linkedContactFilter]);

  const { loading, error, total, items, patchItem, insertItem, removeItem, reload } = usePropertyCollection({
    authorId: null,
    collectionKey: COLLECTION_KEY,
    pagination,
    ordering,
    filters,
    allowWithoutAuthorId: true,
  });

  const handlePropertyFileSave = useCallback(async (variables) => {
    try {
      if (!variables?.id) return variables;
      const result = await apiUpdatePropertyAssetForFiles({ id: variables.id });
      if (result?.id) {
        patchItem(result.id, result);
        if (selectedProperty?.id === result.id) setSelectedPropertyFull(result);
      }
      return result;
    } catch (err) {
      console.error("Error saving property files:", err);
      throw err;
    }
  }, [patchItem, selectedProperty?.id]);

  // Hydrate the selected property with its full document (details etc.).
  useEffect(() => {
    const id = selectedProperty?.id;
    if (!id || selectedProperty?._isNew || String(id).startsWith("draft-")) {
      setSelectedPropertyFull(null);
      return undefined;
    }
    let cancelled = false;
    apiGetPropertyAsset(id)
      .then((doc) => {
        if (!cancelled && doc?.id === id) setSelectedPropertyFull(doc);
      })
      .catch((err) => console.error("Failed to load property details:", err));
    return () => { cancelled = true; };
  }, [selectedProperty?.id, selectedProperty?._isNew]);

  const selectedPropertyRaw = useMemo(() => {
    if (!selectedProperty) return null;
    // For a draft property (created via "+" button) the item isn't in the
    // fetched list yet — fall back to the draft itself so the detail panel renders.
    if (selectedProperty._isNew) return selectedProperty;
    const listItem = items.find((p) => p.id === selectedProperty.id) || null;
    const full = selectedPropertyFull?.id === selectedProperty.id ? selectedPropertyFull : null;
    // List item fields win (they carry local optimistic patches); the full doc
    // contributes the heavyweight fields (`details`, `ref`) the list omits.
    if (listItem && full) return { ...full, ...listItem };
    return listItem || full;
  }, [selectedProperty, items, selectedPropertyFull]);

  useEffect(() => {
    if (!selectedProperty?.id || selectedProperty?._isNew) return;
    const hydrated = items.find((p) => p.id === selectedProperty.id);
    if (hydrated) setSelectedProperty(hydrated);
  }, [items, selectedProperty?.id, selectedProperty?._isNew]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (selectedProperty?.id && !selectedProperty?._isNew) {
        window.localStorage.setItem(selectedPropertyStorageKey, selectedProperty.id);
      } else if (!selectedProperty) {
        window.localStorage.removeItem(selectedPropertyStorageKey);
      }
    } catch {
      // no-op
    }
  }, [selectedProperty?.id, selectedProperty?._isNew, selectedPropertyStorageKey, selectedProperty]);
  const selectedPropertiesForContacts = useMemo(() => {
    if (selectedPropertyIds.length > 0) {
      return items.filter((p) => selectedPropertyIds.includes(p.id));
    }
    return selectedPropertyRaw ? [selectedPropertyRaw] : [];
  }, [items, selectedPropertyIds, selectedPropertyRaw]);
  const settingsAuthorId = user?.id || user?.userId || "";
  const propertyCategories = usePropertyCategories(settingsAuthorId);
  const propertyStateOptions = usePropertyStateList(settingsAuthorId);
  const propertyStateCounts = usePropertyStateCounts(
    propertyStateOptions,
    activeDomain ? { domain: activeDomain } : {}
  );
  const visiblePropertyStates = useMemo(() => {
    if (!propertyStateCounts) return propertyStateOptions;
    return (propertyStateOptions || []).filter((s) => (propertyStateCounts[s] || 0) > 0);
  }, [propertyStateOptions, propertyStateCounts]);

  const handleStatusChange = useCallback(
    async (property, nextStatus) => {
      if (!property?.id) return;
      const value = String(nextStatus || "");
      patchItem(property.id, { status: value });
      try {
        // NOTE: list items are slim (no `details`), so never send a details
        // payload from here — it would replace the stored details object.
        await apiUpdatePropertyAsset({ id: property.id, status: value });
      } catch (err) {
        console.error("Failed to update property status:", err);
      }
    },
    [patchItem]
  );

  const handleCategoryChange = useCallback(
    async (property, category) => {
      if (!property?.id || !category) return;
      const value = category.id || category.value || category.name || "";
      patchItem(property.id, { category: value });
      try {
        await apiUpdatePropertyAsset({ id: property.id, category: value });
      } catch (err) {
        console.error("Failed to update property category:", err);
      }
    },
    [patchItem]
  );

  const handleAddProperty = useCallback(() => {
    setSelectedProperty(newPropertyDraft());
    setPanels({ details: true, map: false, comments: false, chat: false });
  }, [setPanels]);

  const handleSaveProperty = useCallback(
    async (updated) => {
      if (!updated) return;
      if (updated._isNew) {
        try {
          const { name, status, category, identity, details } = updated;
          const created = await apiCreatePropertyAsset({
            authorId,
            domain: activeDomain || userDomain || "",
            name: name || "New Property",
            ...(status ? { status } : {}),
            ...(category ? { category } : {}),
            ...(identity ? { identity } : {}),
            ...(details ? { details } : {}),
          });
          if (created?.id) {
            setSelectedProperty(created);
            setSelectedPropertyFull(created);
            // Patch-in-place when the new doc is visible as-is (page 1, no
            // filters); otherwise let the server re-derive the page.
            if (page === 1 && activeFilters.length === 0) insertItem(created);
            else reload?.();
          }
        } catch (err) {
          console.error("Failed to create property:", err);
        }
        return;
      }
      if (!updated.id) return;
      patchItem(updated.id, updated);
      try {
        const { id, name, status, category, identity, details } = updated;
        const result = await apiUpdatePropertyAsset({ id, name, status, category, identity, details });
        if (result?.id) {
          patchItem(result.id, result);
          setSelectedPropertyFull(result);
        }
      } catch (err) {
        console.error("Failed to save property:", err);
      }
    },
    [patchItem, insertItem, authorId, activeDomain, userDomain, reload, page, activeFilters]
  );

  const handleCopyProperty = useCallback(
    async (property) => {
      if (!property?.id) return;
      const ts = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .slice(0, 19);
      const baseName = property.name || "Untitled";
      const copyName = `copy-${ts} ${baseName}`;
      try {
        // List items are slim (no `details`) — copy from the full document.
        const source =
          (property.details ? property : null) ||
          (selectedPropertyFull?.id === property.id ? selectedPropertyFull : null) ||
          (await apiGetPropertyAsset(property.id)) ||
          property;
        const created = await apiCreatePropertyAsset({
          authorId,
          domain: source.domain || activeDomain || userDomain || "",
          name: copyName,
          ...(source.status ? { status: source.status } : {}),
          ...(source.category ? { category: source.category } : {}),
          ...(source.identity ? { identity: source.identity } : {}),
          ...(source.details ? { details: source.details } : {}),
        });
        if (created?.id) {
          setSelectedProperty(created);
          setSelectedPropertyFull(created);
          if (page === 1 && activeFilters.length === 0) insertItem(created);
          else reload?.();
        }
      } catch (err) {
        console.error("Failed to copy property:", err);
        window.alert(`Failed to copy property: ${err?.message || err}`);
      }
    },
    [authorId, activeDomain, userDomain, reload, insertItem, page, activeFilters, selectedPropertyFull]
  );

  const handleDeleteProperty = useCallback(
    async (property) => {
      if (!property?.id) return;
      const originalId = property.id;
      try {
        await apiDeletePropertyAsset(originalId);
        removeItem(originalId);
        if (selectedProperty?.id === originalId) setSelectedProperty(null);
        setSelectedPropertyIds((prev) => prev.filter((id) => id !== originalId));
      } catch (err) {
        console.error("Failed to delete property:", err);
        window.alert(`Failed to delete property: ${err?.message || err}`);
        reload?.();
      }
    },
    [removeItem, selectedProperty?.id, reload]
  );
  const handleTogglePropertySelection = useCallback((property) => {
    const id = property?.id;
    if (!id) return;
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);
  const handleAddPropertyContact = useCallback(() => {
    setSelectedContact({
      id: `draft-${Date.now()}`,
      ...EMPTY_CONTACT_DRAFT,
      _isNew: true,
    });
    setShowContactDetails(true);
    setPanels({ details: true, map: false, comments: false, chat: false });
  }, [setPanels]);

  // Contact rows from PropertyContactListPanel are slim (no `data` bag).
  // Hydrate with the full contact before the detail panel edits/saves it.
  useEffect(() => {
    const id = selectedContact?.id;
    if (!showContactDetails || !id || selectedContact?._isNew || String(id).startsWith("draft-")) {
      return undefined;
    }
    let cancelled = false;
    apiGetContactById({ id, authorId })
      .then((doc) => {
        if (!cancelled && doc?.id) {
          setSelectedContact((prev) => (prev?.id === doc.id ? { ...prev, ...doc } : prev));
        }
      })
      .catch((err) => console.error("Failed to load contact details:", err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showContactDetails, selectedContact?.id, selectedContact?._isNew, authorId]);

  const handleSavePropertyContact = useCallback(async (changes) => {
    if (!selectedContact) return;
    try {
      if (selectedContact._isNew) {
        const property = selectedProperty?.id
          ? items.find((p) => p.id === selectedProperty.id) || selectedProperty
          : null;
        const propertyDomain = property?.domain || activeDomain || undefined;
        const created = await apiCreateContact({
          authorId,
          ...(propertyDomain ? { domain: propertyDomain } : {}),
          ...EMPTY_CONTACT_DRAFT,
          ...changes,
        });
        const createdDoc = created?.contact || created;
        if (createdDoc?.id) {
          setSelectedContact(createdDoc);

          if (property?.id) {
            try {
              // The selected property may be a slim stub (e.g. restored by id
              // when it is not on the current list page) with no contactList.
              // Always merge against the server copy so existing links are
              // never overwritten.
              let existing = Array.isArray(property.contactList) ? property.contactList : null;
              try {
                const fresh = await apiGetPropertyAsset(property.id);
                if (fresh?.id === property.id) {
                  existing = Array.isArray(fresh.contactList) ? fresh.contactList : [];
                }
              } catch (fetchErr) {
                console.error("Failed to refresh property before linking contact:", fetchErr);
              }
              if (existing === null) {
                throw new Error("Property contact list unavailable; link not written to avoid data loss.");
              }
              if (!existing.some((e) => e?.contactId === createdDoc.id)) {
                const nextContactList = [
                  ...existing.map((e) => ({ contactId: e.contactId, role: e.role || "" })),
                  { contactId: createdDoc.id, role: "" },
                ];
                const updated = await apiUpdatePropertyAsset({
                  id: property.id,
                  contactList: nextContactList,
                });
                const contactList = updated?.contactList || nextContactList;
                patchItem(property.id, { contactList });
                setSelectedProperty((prev) =>
                  prev && prev.id === property.id ? { ...prev, contactList } : prev
                );
                setSelectedPropertyFull((prev) =>
                  prev && prev.id === property.id ? { ...prev, contactList } : prev
                );
              }
            } catch (linkErr) {
              console.error("Failed to link contact to property:", linkErr);
            }
          }
        }
      } else if (selectedContact?.id) {
        await apiUpdateContact({ id: selectedContact.id, ...changes });
      }
    } catch (err) {
      console.error("Failed to save contact:", err);
    }
  }, [authorId, activeDomain, selectedContact, selectedProperty, items, patchItem]);

  const handleDeletePropertyContact = useCallback(async (contactId) => {
    if (!contactId) return;
    if (String(contactId).startsWith("draft-")) {
      setSelectedContact(null);
      setShowContactDetails(false);
      return;
    }
    try {
      await apiDeleteContact({ id: contactId, authorId });
      setSelectedContact(null);
      setShowContactDetails(false);
    } catch (err) {
      console.error("Failed to delete contact:", err);
    }
  }, [authorId]);
  const properties = useMemo(() => {
    if (!categoryFilter) return items.map(toPropertyCard);
    const selected = propertyCategories.find((c) => {
      const target = normalizeText(categoryFilter);
      return (
        normalizeText(c?.id) === target ||
        normalizeText(c?.value) === target ||
        normalizeText(c?.name) === target ||
        normalizeText(c?.label) === target
      );
    });
    const needles = new Set(
      [
        categoryFilter,
        selected?.id,
        selected?.value,
        selected?.name,
        selected?.label,
      ].map(normalizeText).filter(Boolean)
    );
    return items
      .filter((p) => needles.has(normalizeText(p?.category)))
      .map(toPropertyCard);
  }, [items, categoryFilter, propertyCategories]);
  const visibleTotal = categoryFilter ? properties.length : total;
  const totalPages = Math.max(1, Math.ceil(visibleTotal / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  useEffect(() => {
    const validIds = new Set(items.map((p) => p.id).filter(Boolean));
    setSelectedPropertyIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [items]);

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  const handleSearchSubmit = useCallback(() => {
    setSearchFilter(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const handleSearchChange = useCallback((v) => {
    setSearchInput(v);
    if (!v.trim()) {
      setSearchFilter("");
      setPage(1);
    }
  }, []);

  useEffect(() => {
    setPropertiesToolbar({
      searchQuery: searchInput || "",
      onSearchChange: handleSearchChange,
      onSearchSubmit: handleSearchSubmit,
    });
    return () => {
      resetPropertiesToolbar();
    };
  }, [searchInput, handleSearchChange, handleSearchSubmit, setPropertiesToolbar, resetPropertiesToolbar]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        gap: isMobile ? 0.75 : 1,
      }}
    >
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 500}
        minWidth={isMobile ? window.innerWidth : 380}
        maxWidth={isMobile ? window.innerWidth : 760}
        fillRemaining={isMobile || lastPanel === null}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <PropertyListPanel
          loading={loading}
          error={error}
          properties={properties}
          total={visibleTotal}
          page={page}
          totalPages={totalPages}
          canPrevious={page > 1}
          canNext={page < totalPages}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
          searchQuery={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          selectedId={selectedProperty?.id}
          selectedIds={selectedPropertyIds}
          onSelect={setSelectedProperty}
          onOpenDetails={(property) => {
            setSelectedProperty(property);
            setShowContactDetails(false);
            setPanels({ details: true, map: false, comments: false, chat: false });
          }}
          onAdd={handleAddProperty}
          onIngest={() => setIngestDialogOpen(true)}
          onToggleSelect={handleTogglePropertySelection}
          activeFilters={activeFilters}
          linkedContactFilter={linkedContactFilter}
          onClearLinkedContactFilter={() => setLinkedContactFilter(null)}
          categories={propertyCategories}
          availableStatuses={AVAILABLE_PROPERTY_STATUSES}
          onStatusChange={handleStatusChange}
          onCategoryChange={handleCategoryChange}
          onDeleteProperty={handleDeleteProperty}
          onCopyProperty={handleCopyProperty}
          pageSize={rowsPerPage}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={(value) => {
            setCategoryFilter(value || "");
            setPage(1);
          }}
          propertyStatusFilter={propertyStatusFilter}
          onPropertyStatusFilterChange={(value) => {
            setPropertyStatusFilter(value || "");
            setPage(1);
          }}
          propertyStateFilter={propertyStateFilter}
          onPropertyStateFilterChange={(value) => {
            setPropertyStateFilter(value || "");
            setPage(1);
          }}
          availablePropertyStates={visiblePropertyStates}
          propertyStateCounts={propertyStateCounts}
        />
      </ResizablePanel>

      {contactsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 360}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 560}
          fillRemaining={!isMobile && lastPanel === "contacts"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyContactListPanel
            property={selectedPropertyRaw}
            selectedProperties={selectedPropertiesForContacts}
            authorId={authorId}
            onClose={() => togglePanel("contacts")}
            selectedId={selectedContact?.id}
            onSelect={(contact) => {
              setSelectedContact(contact);
              setShowContactDetails(false);
            }}
            onViewAllProperties={(contact) => {
              const ids = Array.isArray(contact?.propertyListIds) ? contact.propertyListIds : [];
              if (!ids.length) return;
              const name = (() => {
                const p = contact?.identity?.person || {};
                return `${p.firstName || ""} ${p.lastName || ""}`.replace(/\s+/g, " ").trim()
                  || contact?.identity?.company?.name || "(contact)";
              })();
              setLinkedContactFilter({
                contactId: String(contact?._id || contact?.id || ""),
                contactName: name,
                propertyIds: ids.map(String),
              });
            }}
            onAdd={handleAddPropertyContact}
            onOpenDetails={(contact) => {
              setSelectedContact(contact);
              setShowContactDetails(true);
              setPanels({ details: true, map: false, comments: false, chat: false });
            }}
            onCompose={(contact) => {
              setComposeContact(contact);
            }}
            onComposeText={(contact) => {
              setComposeTextContact(contact);
            }}
            onMagic={() => {
              if (selectedContact) {
                setComposeContact(selectedContact);
              }
            }}
            onBulkEmail={(recipients) => {
              setBulkEmail({ open: true, recipients: Array.isArray(recipients) ? recipients : [] });
            }}
            onDelete={handleDeletePropertyContact}
          />
        </ResizablePanel>
      )}

      {detailsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "details"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          {showContactDetails ? (
            <ContactDetailPanel
              contact={selectedContact}
              onClose={() => togglePanel("details")}
              onSave={handleSavePropertyContact}
              onDelete={handleDeletePropertyContact}
            />
          ) : (
            <PropertyDetailPanel
              property={selectedPropertyRaw}
              onSave={handleSaveProperty}
              onCopy={handleCopyProperty}
              onClose={() => togglePanel("details")}
            />
          )}
        </ResizablePanel>
      )}

      {filesOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 900}
          fillRemaining={!isMobile && lastPanel === "files"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <FilesPanel
            onClose={() => togglePanel("files")}
            propertyInfo={selectedPropertyRaw || { id: "", details: { files: [] } }}
            updatePropertyInfo={() => {}}
            uploadSingle={uploadSingle}
            actionSave={handlePropertyFileSave}
            filesDisplayName={filesDisplayName}
            setFilesDisplayName={setFilesDisplayName}
            filesSelectedType={filesSelectedType}
            setFilesSelectedType={setFilesSelectedType}
            filesSelectedFile={filesSelectedFile}
            setFilesSelectedFile={setFilesSelectedFile}
            filesStatus={filesStatus}
            setFilesStatus={setFilesStatus}
            filesProgress={filesProgress}
            setFilesProgress={setFilesProgress}
            fileSettingsType="property"
            fileRefCollection="propertyAssets"
            fileRefSchemaType="propertyAsset"
            emptySelectionMessage="Select property first"
            previewEntityType="property"
            entityName={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
          />
        </ResizablePanel>
      )}

      {imagesOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "images"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyImagesPanel
            onClose={() => togglePanel("images")}
            propertyInfo={selectedPropertyRaw || { id: "", details: { files: [] } }}
            uploadSingle={uploadSingle}
            fileRefCollection="propertyAssets"
            emptySelectionMessage="Select property first"
            previewEntityType="property"
            entityName={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
          />
        </ResizablePanel>
      )}

      {verifyOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 480}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 700}
          fillRemaining={!isMobile && lastPanel === "verify"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyVerifyPanel
            property={selectedPropertyRaw}
            onClose={() => togglePanel("verify")}
            onResolved={() => reload?.()}
          />
        </ResizablePanel>
      )}

      {mapOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "map"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyMapPanel
            selectedProperties={properties.filter((p) => selectedPropertyIds.includes(p.id))}
            fallbackProperty={selectedPropertyRaw}
            onClose={() => togglePanel("map")}
          />
        </ResizablePanel>
      )}

      {commentsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "comments"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <CommentsPanel
            refCollection={PROPERTY_REF_COLLECTION}
            refId={selectedPropertyRaw?.id || null}
            refLabel={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
            emptyMessage="Select a property to view comments."
            authorId={authorId}
            onClose={() => togglePanel("comments")}
          />
        </ResizablePanel>
      )}

      {chatOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "chat"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyChatPanel
            property={selectedPropertyRaw}
            authorId={authorId}
            onClose={() => togglePanel("chat")}
          />
        </ResizablePanel>
      )}

      {journalOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "journal"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <JournalPanel
            refCollection={PROPERTY_REF_COLLECTION}
            refId={selectedPropertyRaw?.id || null}
            refName={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
            emptyMessage="Select a property to view its journal"
            authorId={authorId}
            onClose={() => togglePanel("journal")}
          />
        </ResizablePanel>
      )}

      {remindersOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "reminders"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <RemindersPanel
            authorId={authorId}
            refCollection={PROPERTY_REF_COLLECTION}
            onClose={() => togglePanel("reminders")}
          />
        </ResizablePanel>
      )}

      <ComposeWithMagicDialog
        open={Boolean(composeContact)}
        onClose={() => setComposeContact(null)}
        contact={composeContact}
      />
      <ComposeTextDialog
        open={Boolean(composeTextContact)}
        onClose={() => setComposeTextContact(null)}
        contact={composeTextContact}
      />
      <SendEmailToCategoryDialog
        open={bulkEmail.open}
        onClose={() => setBulkEmail({ open: false, recipients: [] })}
        recipients={bulkEmail.recipients}
        authorId={authorId}
        domain={user?.domain || ""}
        defaultFrom={user?.email || ""}
        title="Bulk Email — Property Contacts"
      />
      <IngestFileDialog
        open={ingestDialogOpen}
        onClose={() => setIngestDialogOpen(false)}
        domain={activeDomain || userDomain}
        authorId={authorId}
        uploadSingle={uploadSingle}
      />
    </Box>
  );
};

export default PageProperties;
